import { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';

import classes from "./BookingPage.module.css";
import NavBar from "../../components/NavBar/NavBar";
import NavBarPhone from "../../components/NavBarPhone/NavBarPhone";
import BookedCard from "../../components/BookingPage/BookedCard";
import BookedCardPhone from "../../components/BookingPage/BookedCardPhone";
import { useAutoRefreshBookings } from '../../hooks/useAutoRefreshBookings';
import { useSelector } from 'react-redux';
import { useBookingExpiry } from '../../hooks/useBookingExpiry';
import { useViewportHeight } from '../../hooks/useViewportHeight';
import useSearchParamsTabs from '../../hooks/useSearchParamsTabs';
import { formatBerlinDate, formatBerlinTime } from '../../util/bookingDateTime';
import { buildCoarseLocationText } from '../../util/locationPrivacy';

/** Query values for ?buchungen=… on /bookings (order: Bevorstehend, Abgeschlossen). */
export const GUEST_BOOKINGS_TAB_IDS = Object.freeze(['bevorstehend', 'abgeschlossen']);

export const BookingPage = () => {
  const TAB_SWITCH_DELAY_MS = 420;
  const { activeIndex: activeTab, setTabIndex } = useSearchParamsTabs({
    paramName: 'buchungen',
    tabIds: GUEST_BOOKINGS_TAB_IDS,
    defaultIndex: 0,
  });
  const [visualTab, setVisualTab] = useState(activeTab);
  const [indicatorStyle, setIndicatorStyle] = useState({});
  const listItemsRef = useRef([]);
  const navRef = useRef(null);
  const tabSwitchTimeoutRef = useRef(null);

  const userID = useSelector(state => state?.auth?.user?.id);

  // Initialize viewport height fix for iOS Safari
  useViewportHeight();

  const { bookingStudioData, loading: isBookingLoading, error: bookingError, refetch } = useAutoRefreshBookings(userID, {
    pollInterval: 30000, // Poll every 30 seconds
    enablePolling: true,
    refreshOnFocus: true,
    refreshOnVisibilityChange: true
  });

  const handleBookingExpired = useCallback((bookingId) => {
    refetch();
  }, [refetch]);

  useBookingExpiry(bookingStudioData?.items || [], handleBookingExpired);

  const organizeBookings = () => {
    if (!bookingStudioData?.items) {
      return { upcoming: [], completed: [], today: [] };
    }

    const now = new Date();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const allBookings = bookingStudioData?.items || [];

    const upcoming = [];
    const completed = [];
    const todayBookings = [];
    const skippedBookings = [];

    allBookings?.forEach(booking => {
      // Bookings that will not happen anymore should always be in "Abgeschlossen"
      const normalizedStatus = String(booking?.status || '').toUpperCase();
      const isExpiredPending = normalizedStatus === 'PENDING' && Boolean(booking?.expired);
      const nonHappeningStatuses = new Set([
        'CANCELLED',
        'CANCELED',
        'WITHDRAWN',
        'EXPIRED',
        'DECLINED',
        'REJECTED',
      ]);
      const isNonHappeningBooking = nonHappeningStatuses.has(normalizedStatus) || isExpiredPending;
      const bookingDateTime = new Date(booking.startDateTime);
      const bookingDate = new Date(booking.startDateTime);
      bookingDate.setHours(0, 0, 0, 0);

      if (isNonHappeningBooking) {
        completed?.push(booking);
        return;
      }

      if (bookingDate?.getTime() === today?.getTime()) {
        if (bookingDateTime <= now) {
          todayBookings?.push(booking);
        } else {
          upcoming?.push(booking);
        }
      } else if (bookingDateTime > now) {
        upcoming?.push(booking);
      } else {
        completed?.push(booking);
      }
    });

    return {
      upcoming: upcoming.sort((a, b) => new Date(a.startDateTime) - new Date(b.startDateTime)),
      completed: completed.sort((a, b) => new Date(b.startDateTime) - new Date(a.startDateTime)),
      today: todayBookings.sort((a, b) => new Date(b.startDateTime) - new Date(a.startDateTime))
    };
  };

  const { upcoming, completed, today } = organizeBookings();
  const bookingLoadErrorMessage = 'Buchungen konnten nicht geladen werden.';

  const getStudioCardData = (booking) => {
    const startTime = formatBerlinTime(booking?.startDateTime, {
      hour: '2-digit',
      minute: '2-digit',
    });
    const endTime = formatBerlinTime(booking?.endDateTime, {
      hour: '2-digit',
      minute: '2-digit',
    });

    return {
      id: booking?.id,
      image: booking?.Studio?.processedImages?.[0],
      name: booking?.Studio?.StudioName,
      location: buildCoarseLocationText(booking?.Studio),
      status: booking?.status,
      guestReviewed: booking?.guestReviewed,
      hostReviewed: booking?.hostReviewed,
      cancelledBy: booking?.cancelledBy,
      createdAt: booking?.createdAt,
      startDateTime: booking?.startDateTime,
      endDateTime: booking?.endDateTime,
      hostName: booking?.Studio?.User?.firstName || '',
      date: formatBerlinDate(booking?.startDateTime, {
        weekday: 'long',
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      }),
      startTime,
      endTime,
      time: `${startTime} - ${endTime}`,
      address: buildCoarseLocationText(booking?.Studio) || `${booking?.Studio?.city || ''}${booking?.Studio?.country ? `, ${booking.Studio.country}` : ''}`,
      hasExactAddress: false,
      price: `${booking?.totalPrice}€`,
      duration: Math.round((new Date(booking?.endDateTime) - new Date(booking?.startDateTime)) / (1000 * 60 * 60)),
      participants: booking?.Studio?.numberOfPeople
    };
  };

  const activeStyle = {
    border: '0',
    background: "var(--light-black)",
    color: 'var(--white)'
  };

  useEffect(() => {
    setVisualTab(activeTab);
  }, [activeTab]);

  useEffect(() => {
    return () => {
      if (tabSwitchTimeoutRef.current) {
        clearTimeout(tabSwitchTimeoutRef.current);
      }
    };
  }, []);

  const handleTabSwitch = (targetTab) => {
    setVisualTab(targetTab);

    if (tabSwitchTimeoutRef.current) {
      clearTimeout(tabSwitchTimeoutRef.current);
    }

    tabSwitchTimeoutRef.current = setTimeout(() => {
      setTabIndex(targetTab);
      tabSwitchTimeoutRef.current = null;
    }, TAB_SWITCH_DELAY_MS);
  };

  useEffect(() => {
      const activeItem = listItemsRef?.current?.[visualTab];
      const navElement = navRef?.current;

      if (activeItem && navElement) {
          const navRect = navElement?.getBoundingClientRect();
          const itemRect = activeItem?.getBoundingClientRect();

        setIndicatorStyle({
            left: `${itemRect.left - navRect.left}px`,
            width: `${itemRect.width}px`
        });
      }
  }, [visualTab]);

  return (
      <div className={classes.bookingPage}>
        <header className={classes.header}>
          <NavBar fromBookings={true} />
        </header>
        <header className={classes.mobileHeader}>
            <Link to="/">
                <svg xmlns="http://www.w3.org/2000/svg" width="53" height="18" viewBox="0 0 53 18" fill="none">
                    <path d="M50.5002 9C51.8809 9 53.0002 7.88071 53.0002 6.5C53.0002 5.11929 51.8809 4 50.5002 4C49.1195 4 48.0002 5.11929 48.0002 6.5C48.0002 7.88071 49.1195 9 50.5002 9Z" fill="#FF003E"/>
                    <path d="M9.93692 9.28445L8.58239 9.17069C5.23081 8.89942 4.10204 8.5494 4.10204 7.7881C4.10204 7.0268 5.52603 6.60677 8.46083 6.60677C11.5606 6.60677 12.8109 7.01805 13.0714 8.12062L13.1148 8.29563H16.5185L16.4838 8.05062C16.1452 5.14542 13.5577 3.73658 8.58239 3.73658C3.60712 3.73658 1.05437 5.11917 1.05437 7.83185C1.05437 10.317 3.09483 11.5158 8.11352 11.9709L9.32912 12.0846C12.6546 12.3734 13.8876 12.8109 13.8876 13.686C13.8876 14.6398 12.3681 15.1298 9.36385 15.1298C5.96017 15.1298 4.2236 14.491 4.05863 13.1697L4.03258 12.9772H0.628906V13.2135C0.785197 16.3899 3.63317 18 9.10336 18C14.5736 18 16.944 16.4949 16.944 13.5285C16.944 10.8858 14.9817 9.69572 9.93692 9.28445Z" fill="#1F1F1F"/>
                    <path d="M42.1415 4.17403V9.8444C42.1415 13.0209 40.0663 14.9897 36.7321 14.9897C33.5976 14.9897 32.3733 13.5809 32.3733 9.95816V4.17403H21.8497V0L18.7152 1.33009V13.2834C18.7152 16.5823 20.2954 17.9912 24.003 17.9912C25.2968 17.9912 26.5384 17.7724 27.589 17.5537L27.7627 17.5187V14.7097L27.5022 14.7622C26.139 15.0422 25.5312 15.1473 24.5587 15.1473C22.7614 15.1473 21.8497 14.7185 21.8497 12.3646V6.7992H30.793L29.1433 8.05928V10.982C29.1433 15.7685 31.1577 17.9912 35.4731 17.9912C38.2603 17.9912 40.5525 17.0199 42.1415 15.1648V17.5537H45.3715V4.17403H42.1415Z" fill="#1F1F1F"/>
                </svg>
            </Link>
        </header>
        <div className={classes.mainContainer}>
          <h1 className="titleSemibold3">Buchungen</h1>
          <div className={classes.navigationContainer}>
            <nav ref={navRef} className={classes.navigationList}>
              <li
                ref={el => listItemsRef.current[0] = el}
                className={`semiboldLarge ${visualTab === 0 ? classes.active : classes.inactive}`}
                onClick={() => handleTabSwitch(0)}
              >
                Bevorstehend
                {upcoming?.length > 0 && (
                  <span className={classes.counterBadge} style={{ backgroundColor: '#15CB15' }}>
                    {upcoming?.length}
                  </span>
                )}
              </li>
              <li
                ref={el => listItemsRef.current[1] = el}
                className={`semiboldLarge ${visualTab === 1 ? classes.active : classes.inactive}`}
                onClick={() => handleTabSwitch(1)}
              >
                Abgeschlossen
                {(completed?.length > 0 || today?.length > 0) && (
                  <span className={classes.counterBadge} style={{ backgroundColor: 'var(--stu-rot)' }}>
                    {(completed?.length || 0) + (today?.length || 0)}
                  </span>
                )}
              </li>
            </nav>
            <div className={classes.indicatorContainer}>
                    <div
                        className={classes.indicator}
                        style={indicatorStyle}
                    />
              <div className={classes.horizontalLine}></div>
            </div>
          </div>
          <div className={classes.contentContainer}>
          {isBookingLoading ? (
            <div className={classes.loadingContainer}>
              <div className={classes.loadingSpinner}></div>
            </div>
          ) : bookingError ? (
            <div className={classes.errorBox}>
              <h3 className="semiboldLarge">Ups!</h3>
              <p className="regularMiddle">{bookingLoadErrorMessage}</p>
            </div>
          ) : (
            <>
              {activeTab === 0 && upcoming?.length > 0 && (
                <div className={classes.bookingsContainer}>
                  {upcoming?.map(booking => {
                    return (
                      <div
                        key={booking?.id}
                      >
                        <BookedCard
                          user={booking?.User?.id}
                          bookingId={booking?.id}
                          creativePackages={booking?.creativePackages}
                          studio={getStudioCardData(booking)}
                        />
                      </div>
                    );
                  })}
                </div>
              )}

              {activeTab === 1 && (completed?.length > 0 || today?.length > 0) && (
                <div className={classes.bookingsContainer}>
                  {today?.length > 0 && (
                    <>
                      <h3 className="semiboldLarge" style={{ marginBottom: '20px', color: 'var(--stu-rot)' }}>Heute</h3>
                      {today?.map(booking => {
                        return (
                          <div
                            key={booking.id}
                          >
                            <BookedCard
                              user={booking?.User?.id}
                              bookingId={booking?.id}
                              creativePackages={booking?.creativePackages}
                              studio={getStudioCardData(booking)}
                            />
                          </div>
                        );
                      })}
                      {completed?.length > 0 && <div style={{ margin: '30px 0', borderTop: '1px solid var(--light-grey)' }}></div>}
                    </>
                  )}
                  {completed?.map(booking => {
                    return (
                      <div
                        key={booking.id}
                      >
                        <BookedCard
                          user={booking?.User?.id}
                          bookingId={booking?.id}
                          creativePackages={booking?.creativePackages}
                          studio={getStudioCardData(booking)}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            {((activeTab === 0 && upcoming?.length === 0) ||
                (activeTab === 1 && completed?.length === 0 && today?.length === 0)) && (
                <div className={classes.emptyContainer}>
                  <div className={classes.emptyStateContainer}>
                      <div className={classes.emptyStateBoxContainer}>
                          <div className={classes.emptyStateBox} />
                          <div className={classes.emptyStateBox} />
                          <div className={classes.emptyStateBox} />
                      </div>
                      <div className={classes.emptyStateContent}>
                          <h2 className="titleSemibold">Noch keine Buchungen</h2>
                          <p className="mediumLarge">Entdecke das passende Studios und buche in wenigen Klicks</p>
                          <Link
                            to="/search"
                            className={`${classes.improveButton} regularSmall`
                          }>
                            Studios entdecken
                          </Link>
                      </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        </div>
        <div className={classes.mainContainerPhone}>
          <h1 className="titleSemibold2">Buchungen</h1>
          <div className={classes.bookingsNavigation}>
            <button
              onClick={() => handleTabSwitch(0)}
              className={`${classes.bookingsNavigationButton} mediumLarge`}
              style={visualTab === 0 ? activeStyle : {}}
            >
              Bevorstehend
              {upcoming?.length > 0 && (
                <span className={classes.counterBadgeMobile} style={{ backgroundColor: '#15CB15' }}>
                  {upcoming?.length}
                </span>
              )}
            </button>
            <button
              onClick={() => handleTabSwitch(1)}
              className={`${classes.bookingsNavigationButton} mediumLarge`}
              style={visualTab === 1 ? activeStyle : {}}
            >
              Abgeschlossen
              {(completed?.length > 0 || today?.length > 0) && (
                <span className={classes.counterBadgeMobile} style={{ backgroundColor: 'var(--stu-rot)' }}>
                  {(completed?.length || 0) + (today?.length || 0)}
                </span>
              )}
            </button>
          </div>
          <div className={classes.horizontalLine}></div>
          <div className={classes.bookingsContainerPhone}>
            {isBookingLoading ? (
              <div className={classes.loadingContainer}>
                <div className={classes.loadingSpinner}></div>
              </div>
            ) : bookingError ? (
              <div className={classes.errorBox}>
                <h3 className="semiboldLarge">Ups!</h3>
                <p className="regularMiddle">{bookingLoadErrorMessage}</p>
              </div>
            ) : (
              <>
                {activeTab === 0 && upcoming?.length > 0 && (
                  <>
                    <h3 className="semiboldLarge">Bevorstehend</h3>
                    {upcoming?.map(booking => {
                      return (
                        <div
                          className={classes.bookingWrapper}
                          key={booking.id}
                        >
                          <BookedCardPhone
                            creativePackages={booking?.creativePackages}
                            user={booking?.User?.id}
                            bookingId={booking?.id}
                            studio={getStudioCardData(booking)}
                          />
                        </div>
                      );
                    })}
                  </>
                )}

                {activeTab === 1 && (completed?.length > 0 || today?.length > 0) && (
                  <>
                    <h3 className="semiboldLarge">Abgeschlossen</h3>
                    {today?.length > 0 && (
                      <>
                        <h4 className="semiboldMedium" style={{ marginTop: '20px', marginBottom: '15px', color: 'var(--stu-rot)' }}>Heute</h4>
                        {today?.map(booking => {
                          return (
                            <div
                              className={classes.bookingWrapper}
                              key={booking?.id}
                            >
                              <BookedCardPhone
                                creativePackages={booking?.creativePackages}
                                user={booking?.User?.id}
                                bookingId={booking?.id}
                                studio={getStudioCardData(booking)}
                              />
                            </div>
                          );
                        })}
                        {completed?.length > 0 && <div style={{ margin: '20px 0', borderTop: '1px solid var(--light-grey)' }}></div>}
                      </>
                    )}
                    {completed?.map(booking => {
                      return (
                        <div
                          className={classes.bookingWrapper}
                          key={booking?.id}
                        >
                          <BookedCardPhone
                            creativePackages={booking?.creativePackages}
                            user={booking?.User?.id}
                            bookingId={booking?.id}
                            studio={getStudioCardData(booking)}
                          />
                        </div>
                      );
                    })}
                  </>
                )}

                {((activeTab === 0 && upcoming?.length === 0) ||
                    (activeTab === 1 && completed?.length === 0 && today?.length === 0)) && (
                      <div className={classes.emptyContainer}>
                      <div className={classes.emptyStateContainer}>
                          <div className={classes.emptyStateBoxContainer}>
                              <div className={classes.emptyStateBox} />
                              <div className={classes.emptyStateBox} />
                              <div className={classes.emptyStateBox} />
                              <div className={classes.emptyStateBox} />
                          </div>
                          <div className={classes.emptyStateContent}>
                              <h2 className="titleSemibold">Noch keine Buchungen</h2>
                              <p className="mediumLarge">Entdecke das passende Studios und buche in wenigen Klicks</p>
                              <Link to="/search" className={`${classes.improveButton} regularSmall`}>
                                Studios entdecken
                              </Link>
                          </div>
                      </div>
                    </div>
                  )}
              </>
            )}
          </div>
        </div>
        <div className={classes.mobileNavBar}>
            <NavBarPhone fromBookings={true} />
        </div>
      </div>
  );
};

export default BookingPage;
